import React, { useEffect, useState, useRef } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAppDispatch, useAppSelector } from '../hooks';
import { fetchProfile, saveProfile } from '../store/slices/profileSlice';
import { HealthProfile, ProfessionalType } from '../types';
import preConditionsData from '../data/preConditions.json';
import {
  getProfilePhotoUploadUrl,
  getCertificationUploadUrl,
  uploadFileToS3,
  submitCertification,
  checkIsAdmin,
  updateProfileType,
} from '../services/api';
import { PhotoCropper } from '../components/common/PhotoCropper';
import './ProfilePage.css';

interface Condition {
  name: string;
  symptoms: string[];
  icd_10: string;
  icd_10_description: string;
  dietary_recommendation: string;
  tags: string[];
}

interface Category {
  category: string;
  conditions: Condition[];
}

const ProfilePage: React.FC = () => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { profile, isLoading, error } = useAppSelector((state) => state.profile);
  const [successMessage, setSuccessMessage] = useState('');
  const [uploadingPhoto, setUploadingPhoto] = useState(false);
  const [uploadingCert, setUploadingCert] = useState(false);
  const [isAdmin, setIsAdmin] = useState(false);
  const photoInputRef = useRef<HTMLInputElement>(null);
  const certInputRef = useRef<HTMLInputElement>(null);

  // Certification form state
  const [certForm, setCertForm] = useState({
    name: '',
    issuingOrganization: '',
    issueDate: '',
    expiryDate: '',
  });
  const [selectedCertFile, setSelectedCertFile] = useState<File | null>(null);

  // Photo cropper state
  const [showCropper, setShowCropper] = useState(false);
  const [imageToCrop, setImageToCrop] = useState<string | null>(null);

  // Tab state for profile sections
  const [activeTab, setActiveTab] = useState<'basic' | 'professional' | 'conditions'>('basic');

  const [formData, setFormData] = useState<HealthProfile>({
    profileType: undefined,
    name: '',
    age: undefined,
    gender: undefined,
    height: undefined,
    weight: undefined,
    activityLevel: undefined,
    preConditions: [],
    isHealthcareProfessional: false,
    professionalType: undefined,
    specialties: [],
    profilePhotoUrl: undefined,
    profileTitle: '',
    bio: '',
    certifications: [],
    verificationStatus: 'not_applicable',
    isVerifiedProvider: false,
    // New fields
    businessEmail: '',
    contactNumbers: ['', '', ''],
    address: '',
    pincode: '',
    city: '',
    state: '',
    consultationMode: undefined,
    onlineConsultationConsent: false,
    onlineConsultationConsentDate: undefined,
  });

  const [expandedCategories, setExpandedCategories] = useState<Set<string>>(new Set());

  useEffect(() => {
    dispatch(fetchProfile());
    // Check if user is admin
    checkIsAdmin().then(result => {
      console.log('Admin check result:', result);
      setIsAdmin(result.isAdmin);
    }).catch(err => {
      console.error('Admin check failed:', err);
      setIsAdmin(false);
    });
  }, [dispatch]);

  useEffect(() => {
    if (profile) {
      console.log('Profile loaded from Redux:', profile);
      console.log('Profile type from Redux:', profile.profileType);
      
      setFormData({
        profileType: profile.profileType,
        name: profile.name || '',
        age: profile.age,
        gender: profile.gender,
        height: profile.height,
        weight: profile.weight,
        activityLevel: profile.activityLevel,
        preConditions: profile.preConditions || [],
        isHealthcareProfessional: profile.isHealthcareProfessional || false,
        professionalType: profile.professionalType,
        specialties: profile.specialties || [],
        profilePhotoUrl: profile.profilePhotoUrl,
        profileTitle: profile.profileTitle || '',
        bio: profile.bio || '',
        certifications: profile.certifications || [],
        verificationStatus: profile.verificationStatus || 'not_applicable',
        isVerifiedProvider: profile.isVerifiedProvider || false,
        // New fields
        businessEmail: profile.businessEmail || '',
        contactNumbers: profile.contactNumbers || ['', '', ''],
        address: profile.address || '',
        pincode: profile.pincode || '',
        city: profile.city || '',
        state: profile.state || '',
        consultationMode: profile.consultationMode,
        onlineConsultationConsent: profile.onlineConsultationConsent || false,
        onlineConsultationConsentDate: profile.onlineConsultationConsentDate,
      });
    }
  }, [profile]);

  // Handle profile photo selection - show cropper
  const handlePhotoSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      alert('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      alert('Image size must be less than 5MB');
      return;
    }

    // Create a URL for the image and show cropper
    const imageUrl = URL.createObjectURL(file);
    setImageToCrop(imageUrl);
    setShowCropper(true);
    
    // Reset the input so the same file can be selected again
    if (photoInputRef.current) {
      photoInputRef.current.value = '';
    }
  };

  // Handle cropped image upload
  const handleCroppedImageUpload = async (croppedBlob: Blob) => {
    setShowCropper(false);
    setUploadingPhoto(true);
    
    // Clean up the object URL
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);
    }

    try {
      // Get presigned URL
      const { uploadUrl, fileUrl } = await getProfilePhotoUploadUrl('profile.jpg', 'image/jpeg');
      
      // Upload cropped image to S3
      await uploadFileToS3(uploadUrl, croppedBlob, 'image/jpeg');
      
      console.log('Photo uploaded to S3, fileUrl:', fileUrl);
      
      // Update form data with new photo URL
      const updatedFormData = { ...formData, profilePhotoUrl: fileUrl };
      setFormData(updatedFormData);
      
      console.log('Saving profile with photo URL:', updatedFormData.profilePhotoUrl);
      
      // Auto-save the profile with the new photo URL
      const result = await dispatch(saveProfile(updatedFormData)).unwrap();
      console.log('Profile save result:', result);
      
      // Refetch profile to confirm it was saved
      dispatch(fetchProfile());
      
      setSuccessMessage('Profile photo updated successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (err) {
      console.error('Photo upload failed:', err);
      alert('Failed to upload photo. Please try again.');
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Handle cropper cancel
  const handleCropperCancel = () => {
    setShowCropper(false);
    if (imageToCrop) {
      URL.revokeObjectURL(imageToCrop);
      setImageToCrop(null);
    }
  };

  // Handle certification file selection
  const handleCertFileSelect = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file) return;

    // Validate file type (PDF, JPG, PNG)
    const validTypes = ['application/pdf', 'image/jpeg', 'image/png'];
    if (!validTypes.includes(file.type)) {
      alert('Please select a PDF or image file (JPG, PNG)');
      return;
    }

    // Validate file size (max 10MB)
    if (file.size > 10 * 1024 * 1024) {
      alert('File size must be less than 10MB');
      return;
    }

    setSelectedCertFile(file);
  };

  // Handle certification submission
  const handleCertSubmit = async () => {
    if (!selectedCertFile || !certForm.name || !certForm.issuingOrganization) {
      alert('Please fill in all required fields and select a file');
      return;
    }

    setUploadingCert(true);
    try {
      // Get presigned URL
      const { uploadUrl, fileUrl } = await getCertificationUploadUrl(
        selectedCertFile.name,
        selectedCertFile.type
      );
      
      // Upload to S3
      await uploadFileToS3(uploadUrl, selectedCertFile);
      
      // Submit for approval
      await submitCertification({
        name: certForm.name,
        issuingOrganization: certForm.issuingOrganization,
        issueDate: certForm.issueDate || undefined,
        expiryDate: certForm.expiryDate || undefined,
        documentUrl: fileUrl,
      });

      // Reset form
      setCertForm({ name: '', issuingOrganization: '', issueDate: '', expiryDate: '' });
      setSelectedCertFile(null);
      if (certInputRef.current) certInputRef.current.value = '';

      // Refresh profile to get updated certifications
      dispatch(fetchProfile());
      
      setSuccessMessage('Certification submitted for approval!');
      setTimeout(() => setSuccessMessage(''), 5000);
    } catch (err) {
      console.error('Certification submission failed:', err);
      alert('Failed to submit certification. Please try again.');
    } finally {
      setUploadingCert(false);
    }
  };

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: name === 'age' || name === 'height' || name === 'weight'
        ? value ? Number(value) : undefined
        : value,
    }));
  };

  const handleProfileTypeChange = async (profileType: 'INDIVIDUAL' | 'BUSINESS') => {
    try {
      console.log('Updating profile type to:', profileType);
      
      // Update local state first to show immediate feedback
      setFormData((prev) => ({ ...prev, profileType }));
      
      // Update profile type on backend
      const response = await updateProfileType(profileType);
      console.log('Backend response:', response);
      
      setSuccessMessage(`Profile type updated to ${profileType}`);
      setTimeout(() => setSuccessMessage(''), 3000);
      
      // Don't refresh the profile - keep the local state to avoid overwriting with stale data
      // The profileType is already updated in formData
    } catch (err) {
      console.error('Failed to update profile type:', err);
      alert('Failed to update profile type. Please try again.');
      // Revert the change if the API call fails
      if (profile) {
        setFormData((prev) => ({ ...prev, profileType: profile.profileType }));
      }
    }
  };

  const toggleCategory = (category: string) => {
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(category)) {
        newSet.delete(category);
      } else {
        newSet.add(category);
      }
      return newSet;
    });
  };

  const handleConditionToggle = (icd10Code: string) => {
    setFormData(prev => {
      const currentConditions = prev.preConditions || [];
      const isSelected = currentConditions.includes(icd10Code);
      
      return {
        ...prev,
        preConditions: isSelected
          ? currentConditions.filter(code => code !== icd10Code)
          : [...currentConditions, icd10Code]
      };
    });
  };

  const isConditionSelected = (icd10Code: string): boolean => {
    return (formData.preConditions || []).includes(icd10Code);
  };

  // Professional types with labels
  const professionalTypes: { value: ProfessionalType; label: string }[] = [
    { value: 'healthcare_professional', label: 'Healthcare Professional' },
    { value: 'certified_trainer', label: 'Certified Trainer' },
    { value: 'ayurvedic_practitioner', label: 'Ayurvedic Practitioner' },
    { value: 'certified_nutritionist', label: 'Certified Nutritionist' },
    { value: 'certified_physiotherapist', label: 'Certified Physiotherapist' },
    { value: 'certified_dentist', label: 'Certified Dentist' },
    { value: 'dermatologist', label: 'Dermatologist' },
    { value: 'sexual_health_expert', label: 'Sexual Health Expert' },
    { value: 'cosmetologist', label: 'Cosmetologist' },
    { value: 'mental_health_counselor', label: 'Mental Health Counselor' },
    { value: 'endocrinologist', label: 'Endocrinologist' },
  ];

  // Specialties by professional type
  const specialtiesByType: Record<ProfessionalType, string[]> = {
    healthcare_professional: [
      'General Medicine',
      'Cardiology',
      'Dermatology',
      'Endocrinology',
      'Gastroenterology',
      'Neurology',
      'Oncology',
      'Pediatrics',
      'Psychiatry',
      'Orthopedics',
      'Ophthalmology',
      'ENT (Otolaryngology)',
      'Obstetrics & Gynecology',
      'Urology',
      'Nephrology',
      'Pulmonology',
      'Rheumatology',
      'Anesthesiology',
      'Radiology',
      'Pathology',
      'Emergency Medicine',
      'Family Medicine',
      'Internal Medicine',
      'Surgery',
      'Nursing',
      'Dentistry',
      'Homeopathy',
    ],
    certified_trainer: [
      'Personal Training',
      'Strength & Conditioning',
      'CrossFit',
      'HIIT Training',
      'Yoga Instruction',
      'Pilates',
      'Sports Conditioning',
      'Bodybuilding',
      'Functional Training',
      'Group Fitness',
      'Senior Fitness',
      'Youth Fitness',
      'Pre/Post Natal Fitness',
      'Rehabilitation Training',
      'Weight Loss Coaching',
      'Athletic Performance',
      'Martial Arts',
      'Swimming/Aquatic Fitness',
      'Dance Fitness',
      'Flexibility & Mobility',
    ],
    ayurvedic_practitioner: [
      'Panchakarma',
      'Kayachikitsa (Internal Medicine)',
      'Balachikitsa (Pediatrics)',
      'Graha Chikitsa (Psychiatry)',
      'Urdhvanga Chikitsa (ENT & Ophthalmology)',
      'Shalya Tantra (Surgery)',
      'Damstra Chikitsa (Toxicology)',
      'Jara Chikitsa (Geriatrics)',
      'Vrushya Chikitsa (Reproductive Health)',
      'Rasayana (Rejuvenation)',
      'Nadi Pariksha (Pulse Diagnosis)',
      'Herbal Medicine',
      'Diet & Nutrition (Ahara)',
      'Yoga & Meditation',
      'Marma Therapy',
      'Abhyanga (Oil Massage)',
      'Shirodhara',
      'Nasya',
      'Basti',
      'Vamana',
    ],
    certified_nutritionist: [
      'Clinical Nutrition',
      'Sports Nutrition',
      'Pediatric Nutrition',
      'Geriatric Nutrition',
      'Weight Management',
      'Diabetes Management',
      'Cardiovascular Nutrition',
      'Renal Nutrition',
      'Oncology Nutrition',
      'Eating Disorders',
      'Food Allergies & Intolerances',
      'Vegan/Vegetarian Nutrition',
      'Prenatal & Postnatal Nutrition',
      'Gut Health & Microbiome',
      'Holistic Nutrition',
      'Functional Nutrition',
      'Corporate Wellness',
      'Community Nutrition',
      'Food Service Management',
      'Nutrigenomics',
    ],
    certified_physiotherapist: [
      'Orthopedic Physiotherapy',
      'Sports Physiotherapy',
      'Neurological Physiotherapy',
      'Cardiopulmonary Physiotherapy',
      'Pediatric Physiotherapy',
      'Geriatric Physiotherapy',
      'Women\'s Health Physiotherapy',
      'Post-Surgical Rehabilitation',
      'Musculoskeletal Therapy',
      'Manual Therapy',
      'Spinal Rehabilitation',
      'Joint Replacement Rehab',
      'Vestibular Rehabilitation',
      'Pain Management',
      'Electrotherapy',
      'Hydrotherapy',
      'Occupational Rehabilitation',
      'Ergonomics',
      'Dry Needling',
      'Kinesiology Taping',
    ],
    certified_dentist: [
      'General Dentistry',
      'Orthodontics',
      'Periodontics (Gum Treatment)',
      'Endodontics (Root Canal)',
      'Prosthodontics (Dentures/Crowns)',
      'Oral Surgery',
      'Pediatric Dentistry',
      'Cosmetic Dentistry',
      'Implant Dentistry',
      'Oral Pathology',
      'Dental Public Health',
      'Oral Medicine',
      'Maxillofacial Surgery',
      'Teeth Whitening',
      'Dental Radiology',
      'Geriatric Dentistry',
      'Emergency Dental Care',
      'TMJ/TMD Treatment',
      'Sleep Dentistry',
      'Laser Dentistry',
    ],
    dermatologist: [
      'Acne Treatment',
      'Psoriasis Management',
      'Eczema Treatment',
      'Hair Loss / Alopecia',
      'Skin Cancer Screening',
      'Cosmetic Dermatology',
      'Laser Skin Treatment',
      'Chemical Peels',
      'Botox & Fillers',
      'Pigmentation Treatment',
      'Anti-Aging Therapy',
      'Scar Treatment',
      'Vitiligo Treatment',
      'Fungal Infections',
      'Allergy Testing',
      'Pediatric Dermatology',
      'Nail Disorders',
      'Rosacea Treatment',
      'Wart Removal',
      'Mole Removal',
    ],
    sexual_health_expert: [
      'Male Sexual Health',
      'Female Sexual Health',
      'Erectile Dysfunction',
      'Premature Ejaculation',
      'Low Libido (Male)',
      'Low Libido (Female)',
      'Sexual Dysfunction',
      'Infertility Counseling',
      'PCOS/PCOD Guidance',
      'Menstrual Health',
      'Menopause Management',
      'Andropause (Male Menopause)',
      'Reproductive Health',
      'STI Awareness & Prevention',
      'Performance Anxiety',
      'Relationship Counseling',
      'Hormonal Imbalance',
      'Fertility Wellness',
      'Pre-Marital Counseling',
      'Sexual Education',
    ],
    cosmetologist: [
      'Facial Treatments',
      'Anti-Aging Treatments',
      'Skin Rejuvenation',
      'Hair Styling & Care',
      'Hair Coloring',
      'Hair Restoration',
      'Nail Care & Art',
      'Makeup Artistry',
      'Bridal Makeup',
      'Body Treatments',
      'Waxing & Threading',
      'Permanent Makeup',
      'Microblading',
      'Lash Extensions',
      'Skin Lightening',
      'Acne Scar Treatment',
      'Tattoo Removal',
      'Body Contouring',
      'Aromatherapy',
      'Spa Treatments',
    ],
    mental_health_counselor: [
      'Anxiety & Stress',
      'Depression',
      'Relationship Issues',
      'Grief & Loss',
      'Trauma & PTSD',
      'Self-Esteem Issues',
      'Anger Management',
      'Addiction Counseling',
      'Eating Disorders',
      'Sleep Disorders',
      'Life Transitions',
      'Career Counseling',
      'Family Therapy',
      'Couples Therapy',
      'Child & Adolescent',
      'OCD Treatment',
      'Panic Disorders',
      'Phobias',
      'Mindfulness & Meditation',
      'Cognitive Behavioral Therapy',
    ],
    endocrinologist: [
      'Diabetes Management',
      'Thyroid Disorders',
      'Hormonal Imbalances',
      'PCOS/PCOD',
      'Adrenal Disorders',
      'Pituitary Disorders',
      'Metabolic Syndrome',
      'Obesity Management',
      'Osteoporosis',
      'Growth Disorders',
      'Testosterone Therapy',
      'Estrogen Therapy',
      'Menopause Management',
      'Male Hypogonadism',
      'Insulin Resistance',
      'Lipid Disorders',
      'Calcium Disorders',
      'Reproductive Endocrinology',
      'Pediatric Endocrinology',
      'Sports Endocrinology',
    ],
  };

  const handleProfessionalTypeSelect = (type: ProfessionalType) => {
    setFormData(prev => {
      const isCurrentlySelected = prev.professionalType === type;
      return {
        ...prev,
        isHealthcareProfessional: !isCurrentlySelected,
        professionalType: isCurrentlySelected ? undefined : type,
        specialties: isCurrentlySelected ? [] : prev.specialties,
      };
    });
  };

  const handleSpecialtyToggle = (specialty: string) => {
    setFormData(prev => {
      const currentSpecialties = prev.specialties || [];
      const isSelected = currentSpecialties.includes(specialty);
      
      return {
        ...prev,
        specialties: isSelected
          ? currentSpecialties.filter(s => s !== specialty)
          : [...currentSpecialties, specialty]
      };
    });
  };

  const isSpecialtySelected = (specialty: string): boolean => {
    return (formData.specialties || []).includes(specialty);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSuccessMessage('');
    
    try {
      await dispatch(saveProfile(formData)).unwrap();
      setSuccessMessage('Profile saved successfully! Redirecting to chat...');
      // Navigate to chat after a brief delay to show success message
      setTimeout(() => {
        navigate('/chat');
      }, 1500);
    } catch (err) {
      // Error is handled by Redux state
    }
  };

  const calculateBMI = () => {
    if (formData.height && formData.weight) {
      const heightInMeters = formData.height / 100;
      const bmi = formData.weight / (heightInMeters * heightInMeters);
      return bmi.toFixed(1);
    }
    return null;
  };

  const bmi = calculateBMI();

  return (
    <div className="profile-page">
      <div className="profile-container">
        <div className="profile-header-row">
          <h1>Health Profile</h1>
          {isAdmin && (
            <button 
              onClick={() => {
                console.log('Navigating to /admin');
                navigate('/admin');
              }} 
              className="admin-dashboard-link"
            >
              🔐 Admin Dashboard
            </button>
          )}
        </div>
        <p className="profile-subtitle">
          Help us personalize your health recommendations by completing your profile
        </p>

        {error && <div className="error-message">{error}</div>}
        {successMessage && <div className="success-message">{successMessage}</div>}

        <form onSubmit={handleSubmit} className="profile-form">
          {/* Profile Type Selection */}
          <div className="form-section profile-type-section">
            <h2>Profile Type</h2>
            <p className="section-description">
              Select whether this is an individual or business profile
            </p>
            <div className="profile-type-selector">
              <div
                className={`profile-type-card ${
                  formData.profileType === 'INDIVIDUAL' ? 'selected' : ''
                }`}
                onClick={() => handleProfileTypeChange('INDIVIDUAL')}
              >
                {formData.profileType === 'INDIVIDUAL' && (
                  <div className="profile-type-badge">✓ SELECTED</div>
                )}
                <div className="profile-type-icon">👤</div>
                <h3>Individual</h3>
                <p>Personal profile for individual users</p>
              </div>
              <div
                className={`profile-type-card ${
                  formData.profileType === 'BUSINESS' ? 'selected' : ''
                }`}
                onClick={() => handleProfileTypeChange('BUSINESS')}
              >
                {formData.profileType === 'BUSINESS' && (
                  <div className="profile-type-badge">✓ SELECTED</div>
                )}
                <div className="profile-type-icon">🏢</div>
                <h3>Business</h3>
                <p>Profile for clinics, gyms, or healthcare facilities</p>
              </div>
            </div>
          </div>

          {/* Business Contact Information - Only for Business profiles */}
          {formData.profileType === 'BUSINESS' && (
            <div className="form-section">
              <h2>🏢 Business Contact Information</h2>
              <p className="section-description">
                Provide your clinic/facility contact details. At least one contact number is required.
              </p>
              
              <div className="form-group">
                <label htmlFor="businessEmail">Business Email *</label>
                <input
                  type="email"
                  id="businessEmail"
                  name="businessEmail"
                  value={formData.businessEmail || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, businessEmail: e.target.value }))}
                  placeholder="clinic@example.com"
                  required={formData.profileType === 'BUSINESS'}
                />
              </div>
              
              <div className="contact-numbers-section">
                <label>Contact Numbers (at least 1 required, up to 3)</label>
                <div className="contact-numbers-grid">
                  <div className="form-group">
                    <input
                      type="tel"
                      placeholder="Primary Contact Number *"
                      value={formData.contactNumbers?.[0] || ''}
                      onChange={(e) => {
                        const numbers = [...(formData.contactNumbers || ['', '', ''])];
                        numbers[0] = e.target.value;
                        setFormData(prev => ({ ...prev, contactNumbers: numbers }));
                      }}
                      required={formData.profileType === 'BUSINESS'}
                      pattern="[0-9]{10}"
                      title="Please enter a 10-digit phone number"
                    />
                    <small className="field-hint">Primary (Required)</small>
                  </div>
                  <div className="form-group">
                    <input
                      type="tel"
                      placeholder="Secondary Contact Number"
                      value={formData.contactNumbers?.[1] || ''}
                      onChange={(e) => {
                        const numbers = [...(formData.contactNumbers || ['', '', ''])];
                        numbers[1] = e.target.value;
                        setFormData(prev => ({ ...prev, contactNumbers: numbers }));
                      }}
                      pattern="[0-9]{10}"
                      title="Please enter a 10-digit phone number"
                    />
                    <small className="field-hint">Secondary (Optional)</small>
                  </div>
                  <div className="form-group">
                    <input
                      type="tel"
                      placeholder="Alternate Contact Number"
                      value={formData.contactNumbers?.[2] || ''}
                      onChange={(e) => {
                        const numbers = [...(formData.contactNumbers || ['', '', ''])];
                        numbers[2] = e.target.value;
                        setFormData(prev => ({ ...prev, contactNumbers: numbers }));
                      }}
                      pattern="[0-9]{10}"
                      title="Please enter a 10-digit phone number"
                    />
                    <small className="field-hint">Alternate (Optional)</small>
                  </div>
                </div>
              </div>

              {/* Business Specialty Selection */}
              <div className="form-group">
                <label>Clinic/Facility Specialty</label>
                <p className="section-description" style={{ marginBottom: '10px' }}>
                  Select your primary specialty to display relevant services
                </p>
                <select
                  value={formData.professionalType || ''}
                  onChange={(e) => setFormData(prev => ({
                    ...prev,
                    professionalType: e.target.value as ProfessionalType || undefined,
                    specialties: [],
                  }))}
                >
                  <option value="">Select Specialty...</option>
                  <option value="healthcare_professional">Medical Clinic</option>
                  <option value="certified_dentist">Dental Clinic</option>
                  <option value="ayurvedic_practitioner">Ayurvedic Center</option>
                  <option value="certified_physiotherapist">Physiotherapy Center</option>
                  <option value="certified_nutritionist">Nutrition & Diet Center</option>
                  <option value="certified_trainer">Fitness Center / Gym</option>
                </select>
              </div>

              {/* Show services based on specialty */}
              {formData.professionalType && specialtiesByType[formData.professionalType] && (
                <div className="specialties-section">
                  <label className="specialties-label">
                    Services Offered (select all that apply):
                  </label>
                  <div className="specialties-grid">
                    {specialtiesByType[formData.professionalType].map((specialty) => (
                      <label key={specialty} className="specialty-item">
                        <input
                          type="checkbox"
                          checked={isSpecialtySelected(specialty)}
                          onChange={() => handleSpecialtyToggle(specialty)}
                        />
                        <span>{specialty}</span>
                      </label>
                    ))}
                  </div>
                  {formData.specialties && formData.specialties.length > 0 && (
                    <div className="selected-specialties-summary">
                      <strong>Selected Services:</strong> {formData.specialties.join(', ')}
                    </div>
                  )}
                </div>
              )}

              <div className="business-mode-notice">
                <div className="notice-icon">📍</div>
                <div className="notice-content">
                  <strong>Offline Services Only</strong>
                  <p>As a business/clinic, your services are available for in-person visits only. Please ensure your address is complete.</p>
                </div>
              </div>
            </div>
          )}

          {/* Address Section - For Business profiles (mandatory) and Professionals (optional) */}
          {(formData.profileType === 'BUSINESS' || formData.isHealthcareProfessional) && (
            <div className="form-section">
              <h2>📍 Location Details</h2>
              <p className="section-description">
                {formData.profileType === 'BUSINESS' 
                  ? 'Provide your clinic/facility address for patients to visit (Required)'
                  : 'Provide your practice address for offline appointments (Optional)'}
              </p>
              
              <div className="form-group">
                <label htmlFor="address">
                  Address {formData.profileType === 'BUSINESS' && '*'}
                </label>
                <textarea
                  id="address"
                  name="address"
                  value={formData.address || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                  placeholder="Enter complete address (Building, Street, Area, Landmark)"
                  rows={3}
                  required={formData.profileType === 'BUSINESS'}
                />
              </div>
              
              <div className="form-row">
                <div className="form-group">
                  <label htmlFor="city">City {formData.profileType === 'BUSINESS' && '*'}</label>
                  <input
                    type="text"
                    id="city"
                    name="city"
                    value={formData.city || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    placeholder="e.g., Mumbai"
                    required={formData.profileType === 'BUSINESS'}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="state">State {formData.profileType === 'BUSINESS' && '*'}</label>
                  <input
                    type="text"
                    id="state"
                    name="state"
                    value={formData.state || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, state: e.target.value }))}
                    placeholder="e.g., Maharashtra"
                    required={formData.profileType === 'BUSINESS'}
                  />
                </div>
              </div>
              
              <div className="form-group">
                <label htmlFor="pincode">
                  Pincode {formData.profileType === 'BUSINESS' && '*'}
                </label>
                <input
                  type="text"
                  id="pincode"
                  name="pincode"
                  value={formData.pincode || ''}
                  onChange={(e) => setFormData(prev => ({ ...prev, pincode: e.target.value }))}
                  placeholder="e.g., 400001"
                  pattern="[0-9]{6}"
                  title="Please enter a valid 6-digit pincode"
                  maxLength={6}
                  required={formData.profileType === 'BUSINESS'}
                />
              </div>
            </div>
          )}

          {/* Profile Photo, Title, and Bio Section */}
          <div className="form-section">
            <h2>Profile Overview</h2>
            
            <div className="profile-photo-section">
              <div className="profile-photo-container">
                {formData.profilePhotoUrl ? (
                  <img 
                    src={formData.profilePhotoUrl} 
                    alt="Profile" 
                    className="profile-photo"
                  />
                ) : (
                  <div className="profile-photo-placeholder">
                    <span>📷</span>
                  </div>
                )}
                <input
                  type="file"
                  ref={photoInputRef}
                  accept="image/*"
                  onChange={handlePhotoSelect}
                  style={{ display: 'none' }}
                />
                <button
                  type="button"
                  className="photo-upload-btn"
                  onClick={() => photoInputRef.current?.click()}
                  disabled={uploadingPhoto}
                >
                  {uploadingPhoto ? 'Uploading...' : 'Upload Photo'}
                </button>
              </div>
              
              <div className="profile-info-fields">
                <div className="form-group">
                  <label htmlFor="profileTitle">Professional Title / Headline</label>
                  <input
                    type="text"
                    id="profileTitle"
                    name="profileTitle"
                    value={formData.profileTitle || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, profileTitle: e.target.value }))}
                    placeholder="e.g., Senior Cardiologist | 15+ Years Experience"
                    maxLength={100}
                  />
                </div>
                
                <div className="form-group">
                  <label htmlFor="bio">Bio / Description</label>
                  <textarea
                    id="bio"
                    name="bio"
                    value={formData.bio || ''}
                    onChange={(e) => setFormData(prev => ({ ...prev, bio: e.target.value }))}
                    placeholder="Tell us about yourself, your experience, and your approach to health & wellness..."
                    rows={4}
                    maxLength={500}
                  />
                  <small className="char-count">{(formData.bio || '').length}/500</small>
                </div>
              </div>
            </div>

            {/* Verification Badge */}
            {formData.isVerifiedProvider && (
              <div className="verification-badge verified">
                ✓ Verified Service Provider
              </div>
            )}
            {formData.verificationStatus === 'pending' && (
              <div className="verification-badge pending">
                ⏳ Verification Pending
              </div>
            )}
          </div>

          {/* Tab Navigation - Only for Individual profiles */}
          {formData.profileType !== 'BUSINESS' && (
            <div className="profile-tabs">
              <button
                type="button"
                className={`profile-tab ${activeTab === 'basic' ? 'active' : ''}`}
                onClick={() => setActiveTab('basic')}
              >
                📋 Basic Info
              </button>
              <button
                type="button"
                className={`profile-tab ${activeTab === 'professional' ? 'active' : ''}`}
                onClick={() => setActiveTab('professional')}
              >
                💼 Professional
              </button>
              <button
                type="button"
                className={`profile-tab ${activeTab === 'conditions' ? 'active' : ''}`}
                onClick={() => setActiveTab('conditions')}
              >
                🏥 Health Conditions
              </button>
            </div>
          )}

          {/* Basic Information Tab - Only for Individual profiles */}
          {formData.profileType !== 'BUSINESS' && activeTab === 'basic' && (
          <div className="form-section">
            <h2>Basic Information</h2>

            <div className="form-group">
              <label htmlFor="name">Preferred Name</label>
              <input
                type="text"
                id="name"
                name="name"
                value={formData.name}
                onChange={handleChange}
                placeholder="How should we address you?"
                maxLength={50}
              />
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="age">Age (years)</label>
                <input
                  type="number"
                  id="age"
                  name="age"
                  value={formData.age || ''}
                  onChange={handleChange}
                  placeholder="35"
                  min="1"
                  max="120"
                />
              </div>

              <div className="form-group">
                <label htmlFor="gender">Gender</label>
                <select
                  id="gender"
                  name="gender"
                  value={formData.gender || ''}
                  onChange={handleChange}
                >
                  <option value="">Select...</option>
                  <option value="male">Male</option>
                  <option value="female">Female</option>
                  <option value="other">Other</option>
                </select>
              </div>
            </div>

            <div className="form-row">
              <div className="form-group">
                <label htmlFor="height">Height (cm)</label>
                <input
                  type="number"
                  id="height"
                  name="height"
                  value={formData.height || ''}
                  onChange={handleChange}
                  placeholder="175"
                  min="50"
                  max="250"
                />
              </div>

              <div className="form-group">
                <label htmlFor="weight">Weight (kg)</label>
                <input
                  type="number"
                  id="weight"
                  name="weight"
                  value={formData.weight || ''}
                  onChange={handleChange}
                  placeholder="70"
                  min="20"
                  max="300"
                />
              </div>
            </div>

            {bmi && (
              <div className="bmi-display">
                <strong>BMI:</strong> {bmi} 
                <span className="bmi-category">
                  {Number(bmi) < 18.5 ? ' (Underweight)' :
                   Number(bmi) < 25 ? ' (Normal)' :
                   Number(bmi) < 30 ? ' (Overweight)' : ' (Obese)'}
                </span>
              </div>
            )}

            {/* Activity & Lifestyle - Part of Basic Info Tab */}
            <h3 style={{ marginTop: '2rem' }}>Activity & Lifestyle</h3>

            <div className="form-group">
              <label htmlFor="activityLevel">Activity Level</label>
              <select
                id="activityLevel"
                name="activityLevel"
                value={formData.activityLevel || ''}
                onChange={handleChange}
              >
                <option value="">Select...</option>
                <option value="sedentary">Sedentary (Little or no exercise)</option>
                <option value="light">Light (Exercise 1-3 days/week)</option>
                <option value="moderate">Moderate (Exercise 3-5 days/week)</option>
                <option value="active">Active (Exercise 6-7 days/week)</option>
                <option value="very_active">Very Active (Physical job or 2x training/day)</option>
              </select>
            </div>
          </div>
          )}

          {/* Professional Background Tab - Only for Individual profiles */}
          {formData.profileType !== 'BUSINESS' && activeTab === 'professional' && (
          <div className="form-section">
            <h2>Professional Background</h2>
            <p className="section-description">
              Are you a certified professional? Select your certification type to help us understand your background.
            </p>

            <div className="professional-types-grid">
              {professionalTypes.map((profType) => (
                <label key={profType.value} className="professional-type-item">
                  <input
                    type="radio"
                    name="professionalType"
                    checked={formData.professionalType === profType.value}
                    onChange={() => handleProfessionalTypeSelect(profType.value)}
                  />
                  <span>{profType.label}</span>
                </label>
              ))}
              <label className="professional-type-item">
                <input
                  type="radio"
                  name="professionalType"
                  checked={!formData.professionalType}
                  onChange={() => setFormData(prev => ({
                    ...prev,
                    isHealthcareProfessional: false,
                    professionalType: undefined,
                    specialties: [],
                  }))}
                />
                <span>None of the above</span>
              </label>
            </div>

            {formData.professionalType && (
              <div className="specialties-section">
                <label className="specialties-label">
                  Select your specialties:
                </label>
                <div className="specialties-grid">
                  {specialtiesByType[formData.professionalType].map((specialty) => (
                    <label key={specialty} className="specialty-item">
                      <input
                        type="checkbox"
                        checked={isSpecialtySelected(specialty)}
                        onChange={() => handleSpecialtyToggle(specialty)}
                      />
                      <span>{specialty}</span>
                    </label>
                  ))}
                </div>
                {formData.specialties && formData.specialties.length > 0 && (
                  <div className="selected-specialties-summary">
                    <strong>Selected:</strong> {formData.specialties.join(', ')}
                  </div>
                )}
              </div>
            )}

            {/* Certification Upload Section - Only for professionals */}
            {formData.professionalType && (
              <div className="certification-upload-section">
                <h3>Upload Your Credentials</h3>
                <p className="certification-description">
                  Upload your certifications and credentials to become a verified service provider.
                  All submissions are reviewed by our admin team.
                </p>

                {/* Verification Status Badge */}
                {formData.verificationStatus && formData.verificationStatus !== 'not_applicable' && (
                  <div className={`verification-status-badge status-${formData.verificationStatus}`}>
                    {formData.verificationStatus === 'pending' && '⏳ Verification Pending'}
                    {formData.verificationStatus === 'approved' && '✅ Verified Provider'}
                    {formData.verificationStatus === 'rejected' && '❌ Verification Rejected'}
                  </div>
                )}

                {/* Existing Certifications */}
                {formData.certifications && formData.certifications.length > 0 && (
                  <div className="existing-certifications">
                    <h4>Your Certifications</h4>
                    {formData.certifications.map((cert, index) => (
                      <div key={index} className={`certification-card status-${cert.status}`}>
                        <div className="cert-header">
                          <span className="cert-name">{cert.name}</span>
                          <span className={`cert-status-badge ${cert.status}`}>
                            {cert.status === 'pending' && '⏳ Pending'}
                            {cert.status === 'approved' && '✅ Approved'}
                            {cert.status === 'rejected' && '❌ Rejected'}
                          </span>
                        </div>
                        <div className="cert-details">
                          <p><strong>Issuing Organization:</strong> {cert.issuingOrganization}</p>
                          <p><strong>Issued:</strong> {cert.issueDate}</p>
                          {cert.expiryDate && <p><strong>Expires:</strong> {cert.expiryDate}</p>}
                        </div>
                        {cert.status === 'rejected' && cert.rejectionReason && (
                          <div className="rejection-reason">
                            <strong>Rejection Reason:</strong> {cert.rejectionReason}
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}

                {/* Upload New Certification Form */}
                <div className="new-certification-form">
                  <h4>Add New Certification</h4>
                  <input
                    type="file"
                    ref={certInputRef}
                    onChange={handleCertFileSelect}
                    accept=".pdf,.jpg,.jpeg,.png"
                    style={{ display: 'none' }}
                  />
                  
                  <div className="cert-form-grid">
                    <div className="form-group">
                      <label>Certification Name *</label>
                      <input
                        type="text"
                        value={certForm.name}
                        onChange={(e) => setCertForm(prev => ({ ...prev, name: e.target.value }))}
                        placeholder="e.g., MD, MBBS, Certified Personal Trainer"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Issuing Organization *</label>
                      <input
                        type="text"
                        value={certForm.issuingOrganization}
                        onChange={(e) => setCertForm(prev => ({ ...prev, issuingOrganization: e.target.value }))}
                        placeholder="e.g., Medical Council of India, ACSM"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Issue Date *</label>
                      <input
                        type="date"
                        value={certForm.issueDate}
                        onChange={(e) => setCertForm(prev => ({ ...prev, issueDate: e.target.value }))}
                      />
                    </div>
                    
                    <div className="form-group">
                      <label>Expiry Date (if applicable)</label>
                      <input
                        type="date"
                        value={certForm.expiryDate}
                        onChange={(e) => setCertForm(prev => ({ ...prev, expiryDate: e.target.value }))}
                      />
                    </div>
                  </div>

                  <div className="cert-file-section">
                    <button
                      type="button"
                      className="select-file-btn"
                      onClick={() => certInputRef.current?.click()}
                    >
                      📄 Select Certificate File
                    </button>
                    {selectedCertFile && (
                      <span className="selected-file-name">
                        Selected: {selectedCertFile.name}
                      </span>
                    )}
                  </div>

                  <button
                    type="button"
                    className="submit-certification-btn"
                    onClick={handleCertSubmit}
                    disabled={uploadingCert || !certForm.name || !certForm.issuingOrganization || !certForm.issueDate || !selectedCertFile}
                  >
                    {uploadingCert ? 'Submitting...' : '📤 Submit for Verification'}
                  </button>
                </div>

                {/* Consultation Mode Selection */}
                <div className="consultation-mode-section">
                  <h4>🩺 Consultation Mode</h4>
                  <p className="section-description">
                    How do you prefer to offer your services to patients/clients?
                  </p>
                  <div className="consultation-mode-options">
                    <label className={`consultation-mode-option ${formData.consultationMode === 'offline' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="consultationMode"
                        value="offline"
                        checked={formData.consultationMode === 'offline'}
                        onChange={() => setFormData(prev => ({ ...prev, consultationMode: 'offline' }))}
                      />
                      <div className="mode-content">
                        <span className="mode-icon">📍</span>
                        <span className="mode-label">Offline Only</span>
                        <span className="mode-desc">In-person visits at your practice</span>
                      </div>
                    </label>
                    <label className={`consultation-mode-option ${formData.consultationMode === 'online' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="consultationMode"
                        value="online"
                        checked={formData.consultationMode === 'online'}
                        onChange={() => setFormData(prev => ({ ...prev, consultationMode: 'online' }))}
                      />
                      <div className="mode-content">
                        <span className="mode-icon">💻</span>
                        <span className="mode-label">Online Only</span>
                        <span className="mode-desc">Video consultations via digital platforms</span>
                      </div>
                    </label>
                    <label className={`consultation-mode-option ${formData.consultationMode === 'both' ? 'selected' : ''}`}>
                      <input
                        type="radio"
                        name="consultationMode"
                        value="both"
                        checked={formData.consultationMode === 'both'}
                        onChange={() => setFormData(prev => ({ ...prev, consultationMode: 'both' }))}
                      />
                      <div className="mode-content">
                        <span className="mode-icon">🔄</span>
                        <span className="mode-label">Both</span>
                        <span className="mode-desc">Offline & Online consultations</span>
                      </div>
                    </label>
                  </div>
                </div>

                {/* Online Consultation Consent - One time only */}
                {(formData.consultationMode === 'online' || formData.consultationMode === 'both') && 
                 !formData.onlineConsultationConsent && (
                  <div className="online-consent-section">
                    <div className="consent-box">
                      <h4>📋 Online Consultation Agreement</h4>
                      <div className="consent-content">
                        <p>By enabling online consultations, you acknowledge and agree that:</p>
                        <ul>
                          <li>You may be contacted via video conferencing platforms such as <strong>Zoom, Amazon Chime, Google Meet</strong>, or other digital communication tools for providing online consultations/services.</li>
                          <li>You will maintain appropriate professional conduct during all online interactions.</li>
                          <li>You are responsible for ensuring a stable internet connection and appropriate environment for consultations.</li>
                          <li>Patient/client privacy and confidentiality will be maintained during online sessions.</li>
                          <li>You agree to comply with all applicable telehealth regulations and guidelines.</li>
                        </ul>
                      </div>
                      <label className="consent-checkbox">
                        <input
                          type="checkbox"
                          checked={formData.onlineConsultationConsent || false}
                          onChange={(e) => setFormData(prev => ({
                            ...prev,
                            onlineConsultationConsent: e.target.checked,
                            onlineConsultationConsentDate: e.target.checked ? new Date().toISOString() : undefined,
                          }))}
                        />
                        <span>I have read and agree to the above terms for online consultations</span>
                      </label>
                    </div>
                  </div>
                )}

                {/* Show consent already given */}
                {formData.onlineConsultationConsent && formData.onlineConsultationConsentDate && (
                  <div className="consent-given-notice">
                    <span className="consent-icon">✅</span>
                    <span>Online consultation agreement accepted on {new Date(formData.onlineConsultationConsentDate).toLocaleDateString()}</span>
                  </div>
                )}
              </div>
            )}
          </div>
          )}

          {/* Pre-Existing Conditions Tab - Only for individual profiles */}
          {(!formData.profileType || formData.profileType === 'INDIVIDUAL') && activeTab === 'conditions' && (
            <div className="form-section">
              <h2>Pre-Existing Conditions</h2>
              <p className="section-description">
                Select any pre-existing conditions or health concerns you may have. 
                This helps us provide more personalized health recommendations.
              </p>

              <div className="conditions-categories">
                {(preConditionsData as Category[]).map((categoryData) => (
                  <div key={categoryData.category} className="category-section">
                    <button
                      type="button"
                      className={`category-header ${expandedCategories.has(categoryData.category) ? 'expanded' : ''}`}
                      onClick={() => toggleCategory(categoryData.category)}
                    >
                      <span className="category-name">{categoryData.category}</span>
                      <span className="category-icon">
                        {expandedCategories.has(categoryData.category) ? '−' : '+'}
                      </span>
                    </button>

                    {expandedCategories.has(categoryData.category) && (
                      <div className="conditions-list">
                        {categoryData.conditions.map((condition) => (
                          <div key={condition.icd_10} className="condition-item">
                            <label className="condition-label">
                              <input
                                type="checkbox"
                                checked={isConditionSelected(condition.icd_10)}
                                onChange={() => handleConditionToggle(condition.icd_10)}
                              />
                              <div className="condition-info">
                                <div className="condition-name-line">
                                  <span className="condition-name">{condition.name}</span>
                                  <span className="condition-icd">{condition.icd_10}</span>
                                </div>
                                <div className="condition-symptoms">
                                  {condition.symptoms.join(', ')}
                                </div>
                              </div>
                            </label>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                ))}
              </div>

              {formData.preConditions && formData.preConditions.length > 0 && (
                <div className="selected-conditions-summary">
                  <strong>Selected:</strong> {formData.preConditions.length} condition{formData.preConditions.length !== 1 ? 's' : ''}
                </div>
              )}
            </div>
          )}

          <div className="form-actions">
            <button
              type="submit"
              className="btn-primary"
              disabled={isLoading}
            >
              {isLoading ? 'Saving...' : 'Save Profile'}
            </button>
            <Link to="/change-password" className="btn-secondary change-password-btn">
              🔒 Change Password
            </Link>
          </div>
        </form>

        <div className="profile-info">
          <p className="info-text">
            <strong>Privacy Note:</strong> Your health profile is stored securely and used only to personalize your health assistant responses.
          </p>
        </div>
      </div>

      {/* Photo Cropper Modal */}
      {showCropper && imageToCrop && (
        <PhotoCropper
          image={imageToCrop}
          onCropComplete={handleCroppedImageUpload}
          onCancel={handleCropperCancel}
        />
      )}
    </div>
  );
};

export default ProfilePage;
